import * as THREE from 'three';

/**
 * Configuration for the turn over indicator
 */
export interface TurnOverIndicatorConfig {
  /**
   * Width of the card (used for positioning)
   */
  cardWidth: number;

  /**
   * Height of the card (used for positioning)
   */
  cardHeight: number;

  /**
   * Orientation of the card
   */
  orientation?: 'landscape' | 'portrait';

  /**
   * Text to display (default: "Turn over")
   */
  text?: string;

  /**
   * Font family (default: "Arial, sans-serif")
   */
  font?: string;

  /**
   * Font size in pixels (default: 32)
   */
  fontSize?: number;

  /**
   * Text and arrow color (default: #00ff00 - green)
   */
  color?: string;

  /**
   * Vertical offset from bottom edge of card (default: 0.05)
   */
  offsetFromBottom?: number;

  /**
   * Height above card surface (default: 0.01)
   */
  heightAboveCard?: number;

  /**
   * Scale factor for the indicator (default: 1.0)
   */
  scale?: number;

  /**
   * Initial opacity (default: 1.0)
   */
  opacity?: number;
}

/**
 * Creates a "Turn over" indicator with text and curved arrow
 * Returns a THREE.Group that can be added to any card scene
 */
export class TurnOverIndicator {
  private group: THREE.Group;
  private mesh: THREE.Mesh;
  private texture: THREE.CanvasTexture;
  private material: THREE.MeshBasicMaterial;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private config: Required<TurnOverIndicatorConfig>;

  constructor(config: TurnOverIndicatorConfig) {
    // Set defaults
    this.config = {
      cardWidth: config.cardWidth,
      cardHeight: config.cardHeight,
      orientation: config.orientation ?? 'landscape',
      text: config.text ?? 'Turn over for more',
      font: config.font ?? 'Arial, sans-serif',
      fontSize: config.fontSize ?? 48,
      color: config.color ?? '#00ff00',
      offsetFromBottom: config.offsetFromBottom ?? 0.05,
      heightAboveCard: config.heightAboveCard ?? 0.01,
      scale: config.scale ?? 1.0,
      opacity: config.opacity ?? 1.0
    };

    this.group = new THREE.Group();
    this.group.name = 'turn-over-indicator';

    // Create canvas and texture
    const { canvas, ctx, texture } = this.createCanvasTexture();
    this.canvas = canvas;
    this.ctx = ctx;
    this.texture = texture;

    // Draw the indicator
    this.draw();

    // Create mesh - make it larger so it's visible
    const geometry = new THREE.PlaneGeometry(0.5, 0.15);
    this.material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      opacity: this.config.opacity,
      side: THREE.DoubleSide  // Visible from both sides
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.renderOrder = 100; // Render on top

    // Position at bottom of card
    this.positionIndicator();

    this.group.add(this.mesh);
  }

  /**
   * Create canvas and texture for the indicator
   */
  private createCanvasTexture(): {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
  } {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.needsUpdate = true;

    return { canvas, ctx, texture };
  }

  /**
   * Draw the turn over indicator (text + curved arrow)
   */
  private draw(): void {
    const { canvas, ctx } = this;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw semi-transparent background for visibility
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Set styles
    ctx.fillStyle = this.config.color;
    ctx.strokeStyle = this.config.color;
    ctx.lineWidth = 4;

    // Draw text
    ctx.font = `${this.config.fontSize}px ${this.config.font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.config.text, canvas.width / 2, canvas.height / 2 - 10);

    // Draw curved arrow below text
    this.drawCurvedArrow(
      canvas.width / 2,
      canvas.height / 2 + 20,
      60,
      15
    );

    this.texture.needsUpdate = true;
  }

  /**
   * Draw a curved arrow pointing to the right
   */
  private drawCurvedArrow(
    centerX: number,
    centerY: number,
    width: number,
    arrowSize: number
  ): void {
    const ctx = this.ctx;

    ctx.beginPath();

    // Draw curved line
    const startX = centerX - width / 2;
    const endX = centerX + width / 2;
    const controlY = centerY - 15; // Curve upward

    ctx.moveTo(startX, centerY);
    ctx.quadraticCurveTo(centerX, controlY, endX, centerY);

    ctx.stroke();

    // Draw arrowhead at the end
    const angle = Math.atan2(centerY - controlY, (endX - centerX) / 2);

    ctx.beginPath();
    ctx.moveTo(endX, centerY);
    ctx.lineTo(
      endX - arrowSize * Math.cos(angle - Math.PI / 6),
      centerY - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(endX, centerY);
    ctx.lineTo(
      endX - arrowSize * Math.cos(angle + Math.PI / 6),
      centerY - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  }

  /**
   * Position the indicator at the bottom of the card (outside card boundary)
   */
  private positionIndicator(): void {
    const halfHeight = this.config.cardHeight / 2;
    const y = -halfHeight - this.config.offsetFromBottom;  // move below card edge
    const z = this.config.heightAboveCard; // lift slightly off card surface
    this.mesh.position.set(0, y, z);
    this.mesh.scale.setScalar(this.config.scale);
  }

  /**
   * Get the THREE.Group containing the indicator
   */
  getGroup(): THREE.Group {
    return this.group;
  }

  /**
   * Set the visibility of the indicator
   */
  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  /**
   * Set the opacity of the indicator
   */
  setOpacity(opacity: number): void {
    this.material.opacity = opacity;
  }

  /**
   * Animate the indicator (e.g., fade in/out, pulse)
   */
  animate(type: 'fadeIn' | 'fadeOut' | 'pulse', duration: number = 1.0): void {
    // This is a placeholder for animation logic
    // In a real implementation, you might use GSAP or similar
    if (type === 'fadeIn') {
      this.setOpacity(1.0);
    } else if (type === 'fadeOut') {
      this.setOpacity(0.0);
    } else if (type === 'pulse') {
      // Pulsing animation would be implemented in an update loop
    }
  }

  /**
   * Update the indicator text
   */
  setText(text: string): void {
    this.config.text = text;
    this.draw();
  }

  /**
   * Update the indicator color
   */
  setColor(color: string): void {
    this.config.color = color;
    this.draw();
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    this.texture.dispose();
    this.material.dispose();
    this.mesh.geometry.dispose();

    if (this.group.parent) {
      this.group.parent.remove(this.group);
    }
  }
}

/**
 * Helper function to quickly create a turn over indicator
 */
export function createTurnOverIndicator(config: TurnOverIndicatorConfig): TurnOverIndicator {
  return new TurnOverIndicator(config);
}
